import React, { useEffect, useState } from 'react';
import ActionBar from '../layout/ActionBar';
import CompanyStats from './components/CompanyStats';
import CompanyUsersList from './components/CompanyUsersList';
import { useTranslation } from 'react-i18next';
import SlidingPane from 'react-sliding-pane';

import {
  getCompanyById,
  getStats,
  getAudit,
  addAudit,
  getStatsByDay,
} from '../../actions/companyActions';
import { getKeyProviders } from '../../actions/keyProviderActions';
import { toast } from 'react-toastify';
import Loading from '../common/Loading';
import AuditButton from '../audit/components/AuditButton';
import ResponsiveActionBarButtons from '../common/ResponsiveActionBarButtons';
import DisableKeys from '../keys/slideout/DisableKeys';
import EditCompany from './slideout/EditCompany';
import CompanyDetails from './components/CompanyDetails';
import RequestsTable from '../requests/components/RequestsTable';
import Button from '../common/Button';
import { usePageTitle } from '../../util/pageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks';

export interface CompanyProps {
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
}

const ManageCompany = ({ setCompleteRequestSlideoutOpen }: CompanyProps) => {
  const currentUser = useAppSelector((state) => state.auth);
  const keyProviders = useAppSelector((state) => state.keyProviders);
  const dispatch = useAppDispatch();

  const { id } = useParams();
  const { t } = useTranslation();
  const [pageTitle, setPageTitle] = usePageTitle(['Companies']);
  const [reload, setReload] = useState<any>();
  const navigate = useNavigate();

  const [disableKeysSlideoutOpen, setDisableKeysSlideoutOpen] = useState(false);
  const [editCompanySlideoutOpen, setEditCompanySlideoutOpen] = useState(false);
  const [company, setCompany] = useState<any>();
  const [stats, setStats] = useState<any>();
  const [reloadStats, setReloadStats] = useState<any>();
  const [reloadRequests, setReloadRequests] = useState<any>();

  useEffect(() => {
    if (id) {
      getCompanyById(id)
        .then((d) => {
          setCompany(d);
          setPageTitle([d.companyName, 'Companies']);
        })
        .catch((e) => {
          console.error(e);
          navigate('/companies');
          toast.error(t('Company not found'));
        });
    }
    if (!keyProviders) {
      dispatch(getKeyProviders());
    }
  }, [id, keyProviders, dispatch, reload, t, setPageTitle, navigate]);

  useEffect(() => {
    if (id) {
      getStats(id)
        .then((d) => {
          setStats(d);
        })
        .catch((e) => {
          console.error(e);
          toast.error(t('Failed to get company stats'));
        });
    }
  }, [id, reloadStats, reload, t]);

  return company && stats ? (
    <>
      <ActionBar
        breadcrumb={[
          { link: '/companies', label: t('Companies') },
          { label: company.companyName },
        ]}
      >
        <div className="d-flex">
          <AuditButton id={id} getAudit={getAudit} addAudit={addAudit} />
          <ResponsiveActionBarButtons
            buttons={[
              <Button
                testId="edit-company"
                key="edit"
                className="btn btn-primary d-none d-md-inline-flex ms-2"
                onClick={() => setEditCompanySlideoutOpen(true)}
                text={t('Edit')}
              />,
            ]}
          />
        </div>
      </ActionBar>

      <CompanyDetails company={company} />

      <CompanyStats
        id={company.id}
        stats={stats}
        getData={() => getStatsByDay(company.id)}
      />

      <div className="row">
        <div className="col-sm-12">
          <CompanyUsersList
            id={company.id}
            reload={reload}
            currentUser={currentUser}
          />
        </div>
      </div>

      <RequestsTable
        title={t('Requests made by {company}', {
          company: company.companyName,
        })}
        setCompleteRequestSlideoutOpen={setCompleteRequestSlideoutOpen}
        scope={{ company: id }}
        reload={reloadRequests}
        setReload={setReloadRequests}
        settingsKey={'company-requests-list'}
      />

      <SlidingPane
        isOpen={editCompanySlideoutOpen}
        title={t('Edit {company}', { company: company.companyName })}
        hideHeader={true}
        from="right"
        className="large-side-panel"
        onRequestClose={() => setEditCompanySlideoutOpen(false)}
      >
        <EditCompany
          id={id}
          done={() => {
            setReload(new Date());
            setEditCompanySlideoutOpen(false);
          }}
          cancel={() => setEditCompanySlideoutOpen(false)}
        />
      </SlidingPane>

      <SlidingPane
        isOpen={disableKeysSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setDisableKeysSlideoutOpen(false)}
      >
        <DisableKeys
          title={t('Disable keys for {company}', {
            company: company.companyName,
          })}
          targetCompanyId={id}
          done={() => {
            setDisableKeysSlideoutOpen(false);
            setReloadStats(new Date());
          }}
          cancel={() => setDisableKeysSlideoutOpen(false)}
          defaultReason={t('Disable keys for {company}', {
            company: company.companyName,
          })}
        />
      </SlidingPane>
    </>
  ) : (
    <Loading></Loading>
  );
};

export default ManageCompany;
